import React from 'react';
import { Table, Tabs } from 'antd';
import './styles.scss';
import InsightsCard from '../insightscard';
import {
  AD_TYPE_LABELS,
  PLACEMENT_LABELS,
  SOURCE_LABELS,
  GroupRow,
  percentage,
  sortGroupRows,
  formatCount
} from '../../helpers/inventory-groups';
import type { CountryInventory, InventoryGroupRow } from './types';
import DeliveryMeter, { DeliveryTarget } from './DeliveryMeter';

const { TabPane } = Tabs;

type Props = {
  impressionsByCountry: CountryInventory[];
  campaignType: string;
  /** Set when the query has a target; covers all selected countries together. */
  delivery?: DeliveryTarget | null;
};

const COUNTRY_FLAG_URL = 'https://flagicons.lipis.dev/flags/4x3/';

const formatPercent = (value: number) => `${value}%`;

const breakdownColumns = [
  {
    title: 'Source',
    dataIndex: 'source',
    key: 'source',
    render: (value: GroupRow['source']) => SOURCE_LABELS[value]
  },
  {
    title: 'Ad type',
    dataIndex: 'ad_type',
    key: 'ad_type',
    render: (value: GroupRow['ad_type']) => AD_TYPE_LABELS[value]
  },
  {
    title: 'Placement',
    dataIndex: 'placement',
    key: 'placement',
    render: (value: GroupRow['placement']) => PLACEMENT_LABELS[value]
  },
  {
    title: 'Capacity',
    dataIndex: 'total_impressions',
    key: 'total_impressions',
    align: 'right' as const,
    render: formatCount
  },
  {
    title: 'Booked',
    dataIndex: 'reserved_impressions',
    key: 'reserved_impressions',
    align: 'right' as const,
    render: formatCount
  },
  {
    title: 'In-House',
    dataIndex: 'house_impressions',
    key: 'house_impressions',
    align: 'right' as const,
    render: formatCount
  },
  {
    title: 'Booked %',
    dataIndex: 'str',
    key: 'str',
    align: 'right' as const,
    render: formatPercent
  },
  {
    title: 'Reach',
    dataIndex: 'reach',
    key: 'reach',
    align: 'right' as const,
    render: formatCount
  }
];

const breakdownSummary = (rows: readonly InventoryGroupRow[]) => {
  const total = rows.reduce((sum, row) => sum + row.total_impressions, 0);
  const booked = rows.reduce((sum, row) => sum + row.reserved_impressions, 0);
  const house = rows.reduce((sum, row) => sum + row.house_impressions, 0);
  return (
    <Table.Summary.Row className="inventory-table-total">
      <Table.Summary.Cell index={0} colSpan={3}>
        Total
      </Table.Summary.Cell>
      <Table.Summary.Cell index={3} align="right">
        {formatCount(total)}
      </Table.Summary.Cell>
      <Table.Summary.Cell index={4} align="right">
        {formatCount(booked)}
      </Table.Summary.Cell>
      <Table.Summary.Cell index={5} align="right">
        {formatCount(house)}
      </Table.Summary.Cell>
      <Table.Summary.Cell index={6} align="right">
        {formatPercent(percentage(booked, total))}
      </Table.Summary.Cell>
      <Table.Summary.Cell index={7} align="right">
        {/* Reach is unique users per group and cannot be summed. */}-
      </Table.Summary.Cell>
    </Table.Summary.Row>
  );
};

const CapacitySummary = ({ country }: { country: CountryInventory }) => {
  const unavailablePct = percentage(country.unavailable_impressions, country.total_impressions, 1);
  return (
    <div className="inventory-metrics-row">
      <InsightsCard title="Capacity" width="24%" height="auto">
        <div className="inventory-metric-value">{formatCount(country.total_impressions)}</div>
        <div className="inventory-metric-hint">forecast for the selected targeting</div>
      </InsightsCard>
      <InsightsCard title="Unavailable" width="24%" height="auto">
        <div className="inventory-metric-value">{formatCount(country.unavailable_impressions)}</div>
        <div className="inventory-metric-hint">contending campaigns + GAM&apos;s recent share</div>
      </InsightsCard>
      <InsightsCard title="Available" width="24%" height="auto">
        <div className="inventory-metric-value inventory-metric-highlight">
          {formatCount(country.available_impressions)}
        </div>
        <div className="inventory-metric-hint">capacity minus unavailable</div>
      </InsightsCard>
      <InsightsCard title="Unavailable %" width="24%" height="auto">
        <div className="inventory-metric-value">{formatPercent(unavailablePct)}</div>
        <div className="inventory-reserved-bar">
          <div style={{ width: `${Math.min(unavailablePct, 100)}%` }} />
        </div>
      </InsightsCard>
    </div>
  );
};

const PlacementLine = ({
  country,
  campaignType
}: {
  country: CountryInventory;
  campaignType: string;
}) => (
  <div className="inventory-placement-line">
    <span>
      <b>{formatCount(country.foreground_impressions)}</b> foreground
    </span>
    {campaignType !== 'video' && (
      <span>
        <b>{formatCount(country.background_impressions)}</b> background
      </span>
    )}
    <span>
      <b>{formatCount(country.dfp_impressions)}</b> served by GAM
    </span>
    <span>
      <b>{formatCount(country.reach)}</b> unique users
    </span>
  </div>
);

const VideoEstimate = ({
  country,
  campaignType
}: {
  country: CountryInventory;
  campaignType: string;
}) => {
  // Null when the API could not compute it; undefined from an older API.
  if (country.video_capacity_estimate === null || country.video_capacity_estimate === undefined) {
    return null;
  }
  return (
    <div className={`inventory-video-estimate ${campaignType === 'video' ? 'primary' : ''}`}>
      <div className="inventory-video-estimate-value">
        {formatCount(country.video_capacity_estimate)}
      </div>
      <div>
        <div className="inventory-video-estimate-title">Estimated video capacity</div>
        <div className="inventory-metric-hint">
          Foreground capacity minus audio already booked in the foreground. Video and audio share
          the foreground inventory, so this is an estimate, not a guarantee.
        </div>
      </div>
    </div>
  );
};

const Breakdown = ({ country }: { country: CountryInventory }) => {
  if (!country.breakdown || country.breakdown.length === 0) {
    return (
      <div className="inventory-note">
        The source and placement breakdown is not available with frequency caps or for display ads.
      </div>
    );
  }
  return (
    <div className="inventory-breakdown">
      <div className="inventory-section-title">Breakdown by source, ad type and placement</div>
      <div className="inventory-note">
        Audio and video together, based on the last 7 days and scaled to your date range. Booked
        here is what paying campaigns actually delivered; Unavailable above is contending campaigns
        for the coming period plus GAM&apos;s recent share.
      </div>
      <Table
        className="inventory-table"
        size="small"
        pagination={false}
        rowKey={(row: InventoryGroupRow) => `${row.source}-${row.ad_type}-${row.placement}`}
        columns={breakdownColumns}
        dataSource={sortGroupRows(country.breakdown)}
        summary={(rows) => breakdownSummary(rows as readonly InventoryGroupRow[])}
      />
    </div>
  );
};

export default function InventoryTabbedSection({
  impressionsByCountry,
  campaignType,
  delivery
}: Props) {
  const countryTab = (country: string) => (
    <div className="country-tab">
      {country} <img src={`${COUNTRY_FLAG_URL + country.toLowerCase()}.svg`} alt={country} />
    </div>
  );
  return (
    <div className="inventory-tabbed-section">
      <Tabs defaultActiveKey="0">
        {impressionsByCountry.map((country, index) => (
          <TabPane
            tab={country.country === 'ALL' ? 'Selected Countries' : countryTab(country.country)}
            key={index}
          >
            <div className="tabbed-section-header-text">
              MAXIMUM OF <b>{formatCount(country.available_impressions)}</b> IMPRESSIONS ARE
              AVAILABLE FOR THE SELECTED TARGETING CRITERIA
            </div>
            <CapacitySummary country={country} />
            {/* Display events are all logged as background, so the split means nothing there. */}
            {campaignType !== 'display' && (
              <PlacementLine country={country} campaignType={campaignType} />
            )}
            <div className="inventory-estimates-row">
              <VideoEstimate country={country} campaignType={campaignType} />
              {/* The target is for all selected countries together, so the meter goes on
                  the tab that covers all of them. */}
              {delivery && (country.country === 'ALL' || impressionsByCountry.length === 1) && (
                <div className="inventory-delivery-card">
                  <DeliveryMeter
                    targetImpressions={delivery.target}
                    availableImpressions={delivery.available}
                  />
                </div>
              )}
            </div>
            <Breakdown country={country} />
          </TabPane>
        ))}
      </Tabs>
    </div>
  );
}
